import moment from "moment";
import { difference, merge, get, pick, set, uniqBy } from "lodash";
import { MonextContract, StripeContract, StripeContractStatus } from "../../typings/payment";
import { PREMIUM_DESCRIPTION_MIN_LENGTH, ITEM_DESCRIPTION_MIN_LENGTH } from "../../thor.const";
import { Enums, Events } from "../../thor.enums";
import { LiveOperator, SaleParameters } from "../../typings/live";
import { thor, SalePublishRes } from "../../typings/thor";
import { SaleResult, SaleResultType } from "../../typings/live-api";
import { LocaleCode } from "../../typings/lang";
import { SaleTranslationsStatus, TranslationsStatus } from "../../typings/translations";
import { PremiumStatus } from "../../typings/common";
import { isSaleWithoutShipping } from "../../utils/sale";
import { sleep } from "../../utils/sleep";
import { postMessageToV2 } from "../../utils/sale-iframe";

export class SaleEditModel {
    public static $inject: string[] = [
        "$log",
        "$q",
        "$rootScope",
        "$state",
        "$timeout",
        "$translate",
        "absenteeBidsService",
        "authService",
        "categoryService",
        "contractService",
        "gatewayApi",
        "itemService",
        "liveService",
        "saleService",
        "searchService",
        "sitesService",
        "uploadService",
        "utilsService",
        "configService"
    ];

    public id: number;
    public isEveryMandatoryFieldsFilled: boolean;
    public isSaving: boolean;
    public items: thor.ItemGet[] = [];
    public itemsLoading = false;
    public itemsLoadError: boolean;
    public originalResource: thor.SaleGet;
    public resource: thor.SaleGet;
    public liveResource: { meta: { live: thor.SaleMetaLive }; sites: thor.SaleSites };
    public saleTranslationsStatus: SaleTranslationsStatus;
    public itemDescriptionMinLength: number = ITEM_DESCRIPTION_MIN_LENGTH;
    public stripeEnd: Date;

    protected authorizedAuctionType: any = [Enums.AuctionType.Classic, Enums.AuctionType.Live];
    protected isPublishable: boolean;
    protected liveOperators: LiveOperator[];
    protected liveSale: SaleParameters;
    protected contracts: StripeContract[];
    protected monextContracts: MonextContract[];
    protected isVoluntaryContractActivated: boolean;
    protected selectedTargetLanguage: LocaleCode;

    protected $t;

    protected itemsPublishSummary: {
        [k in keyof { errors; premium_errors }]: {
            orderNbr: string;
            message: string[] | string;
        }[];
    } = {
        errors: [],
        premium_errors: []
    };

    protected subscriptionsCount: number;

    constructor(
        protected $log,
        protected $q,
        protected $rootScope: ng.IRootScopeService,
        protected $state: ng.ui.IStateService,
        protected $timeout: ng.ITimeoutService,
        $translate: ng.translate.ITranslateService,
        private absenteeBidsService,
        private authService,
        private categoryService,
        private contractService,
        protected gatewayApi,
        private itemService,
        private liveService,
        private saleService,
        private searchService,
        private sitesService,
        private uploadService,
        private utils,
        configService
    ) {
        this.$t = $translate.instant;

        const checkAll = () => {
            this.checkIfEveryMandatoryFieldsAreFilled();
            this.checkIsPublishable();
        };

        $rootScope.$on(Events.ItemUpdated, (_, item) => {
            if (item) {
                if (item.status === Enums.ItemStatus.Confirmed) {
                    item.status = Enums.ItemStatus.Modified;
                }

                item.last_updated = new Date();
            }
            checkAll();
        });

        this.stripeEnd = new Date(
            new Date(configService.stripeSaleCreationDeadline).getUTCFullYear(),
            new Date(configService.stripeSaleCreationDeadline).getUTCMonth(),
            new Date(configService.stripeSaleCreationDeadline).getUTCDate()
        );

        $rootScope.$on(Events.PastItemPublished, (_, item) => (this.resource.last_updated = item.last_updated));

        $rootScope.$on(Events.SaleUpdated, checkAll);

        /**
         * Called on modifications on these objects :
         * sale_conditions, sale_results, sale_catalog, Live data
         */
        $rootScope.$on(Events.SaleRelatedObjectUpdated, () => {
            if (this.resource?.status === Enums.SaleStatus.Published) {
                this.resource.status = Enums.SaleStatus.Edited;
            }
            checkAll();
        });

        $rootScope.$on(Events.PostMessageToV2, () => postMessageToV2({ saleId: this.id }));
    }

    public setId(id) {
        this.id = id;
        this.resource = null;
        this.originalResource = null;
        this.items = [];
        this.isPublishable = false;
        this.itemsLoadError = false;
    }

    public fetchAll({ checkSaleTranslationStatus } = { checkSaleTranslationStatus: true }) {
        return this.$q
            .all([this.fetchSale(), this.fetchLiveSale(), this.fetchItems()])
            .then(() => {
                if (checkSaleTranslationStatus) {
                    this.getSaleTranslationStatus();
                }
                this.setSubscriptionsCount();
                this.checkIfEveryMandatoryFieldsAreFilled();
                this.checkIsPublishable();
            })
            .catch(error => this.$log.error(`Error while fetching sale ${this.id}`, error));
    }

    public fetchSale() {
        return this.saleService
            .getSale(this.id)
            .then((resource) => {
                /**
                 * check if sale auction type (classic, live, online)
                 * matches route (`/ventes/*` or `/online-containers/*`)
                 */
                const siteType = resource.sites.interencheres?.type || resource.sites.auction?.type;
                if (!this.authorizedAuctionType.includes(siteType)) {
                    return this.$q.reject("Access to sale or online sale impossible");
                }
                this.resource = this.resourceFormatters(resource);
                this.originalResource = angular.copy(this.resource);
                this.$rootScope.$emit(Events.SaleLoaded);
                return this.$q.resolve();
            })
            .catch(error => this.$log.error(`Error while fetching sale ${this.id}`, error));
    }

    public async fetchSaleParameters(
        { fetchSale, fetchSaleParameters } = { fetchSale: false, fetchSaleParameters: false }
    ) {
        await Promise.all([
            fetchSale ? this.fetchSale() : undefined,
            fetchSaleParameters && this.isLiveSale()
                ? this.fetchLiveSale().then(() => this.$rootScope.$emit(Events.SaleRelatedObjectUpdated))
                : undefined
        ]);
        this.getSaleTranslationStatus();
        this.checkIfEveryMandatoryFieldsAreFilled();
        this.checkIsPublishable();
    }

    public fetchLiveSale() {
        if (!this.isLiveSale()) {
            return this.$q.resolve();
        }

        return this.$q
            .all([
                this.liveService.getLiveOperators(this.authService.organization?.id),
                this.liveService.getLiveSale(this.id)
            ])
            .then(([liveOperators, liveSale]) => {
                this.liveOperators = liveOperators;
                this.liveSale = liveSale;
                return this.$q.resolve();
            });
    }

    /**
     * Set sale liveResource [meta.live, sites] on state change or Fetch from search on direct page load
     * @param {Object} liveMeta data from state change
     * @param {Object} sites data from state change
     * @returns {Promise} resolve
     */
    public setOrFetchLiveResource(
        liveMeta: thor.SaleMetaLive,
        sites: thor.SaleSites
    ): Promise<thor.SaleGet> {
        if (liveMeta === undefined || sites === undefined) {
            const isPast = get(this.resource, "isPast", false);

            return this.searchService
                .searchSaleById(this.id, isPast)
                .then(result => {
                    set(this, "liveResource.meta.live", get(result, "meta.live", null));
                    set(this, "liveResource.sites", get(result, "sites"));
                    return this.$q.resolve();
                });
        }
        set(this, "liveResource.meta.live", liveMeta);
        set(this, "liveResource.sites", sites);
        return this.$q.resolve();
    }

    public fetchItems(suppressed = false) {
        this.itemsLoading = true;
        this.itemsLoadError = false;
        return this.saleService
            .getItems(this.id, suppressed)
            .then(items => {
                items = items.map(item => ({ ...item, suppressed }));
                this.items = suppressed ? [...uniqBy([...items, ...this.items], "id")] : items;
                this.itemsLoadError = false;
                return this.$q.resolve();
            })
            .then(() => {
                const { isPast, sites } = this.resource || {};
                const isSiteLive = this.sitesService.isSiteLive(sites);
                const promises = [];

                this.absenteeBidsService.getAbsenteeBidsCountForItems(this.id);

                if (isSiteLive) {
                    promises.push(
                        this.liveService.getSecretOrders(this.id).then(secretOrders => {
                            this.items.forEach(
                                item =>
                                    (item.secretOrderCount = get(secretOrders, `[${item.id}]count_secret_orders`, 0))
                            );
                            return this.$q.resolve();
                        })
                    );
                }
                if (isSiteLive && isPast) {
                    promises.push(
                        this.gatewayApi
                            .fetchLiveSalesResults(this.id)
                            .then((saleResults: SaleResult[]) => {
                                this.items = this.items.map(item => {
                                    const saleResult = saleResults.find(r => r.item_id === item.id);

                                    if (!item.pricing || saleResult.type === SaleResultType.Unsold) {
                                        item.pricing = { auctioned: { sold: false } };
                                    }
                                    // result already entered rewrites Live saleResult
                                    if (item.pricing.auctioned?.sold && item.pricing.auctioned?.price > 0) {
                                        return item;
                                    }
                                    if (saleResult.amount > 0) {
                                        item.pricing.auctioned = {
                                            price: saleResult.amount,
                                            sold: true
                                        };
                                    }
                                    return item;
                                });
                                return this.$q.resolve();
                            })
                            .catch(() => this.$q.resolve()) // resolve anyway
                    );
                }
                return this.$q.all(promises);
            })
            .catch(error => {
                this.items = [];
                this.itemsLoadError = true;
                return this.$q.reject(error);
            })
            .finally(() => {
                this.itemsLoading = false;
                this.$rootScope.$emit(Events.ItemsLoaded);
            });
    }

    public getSaleTranslationStatus() {
        return new Promise<void>((resolve, reject) => {
            if (this.resource?.isPast) {
                return resolve();
            }
            const getStatus = () =>
                this.saleService
                    .getSaleTranslationStatus(this.id)
                    .then(async res => {
                        this.$log.info(`Sale ${this.id} translations status "${res.status}"`, res);

                        this.setSaleTranslationStatus(res);

                        if (res.status === TranslationsStatus.Done) {
                            return resolve();
                        }

                        if (res.status !== TranslationsStatus.Pending) {
                            return resolve();
                        }
                        await sleep(1000);
                        getStatus();
                    })
                    .catch(e => {
                        this.$log.error(e);
                        this.setSaleTranslationStatus({
                            ...this.saleTranslationsStatus,
                            status: TranslationsStatus.Error
                        });
                        return reject();
                    });
            getStatus();
        });
    }

    public setSaleTranslationStatus(res) {
        this.saleTranslationsStatus = res;
    }

    public getStripeContracts(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isClassicSale()) {
                return resolve();
            }

            return this.contractService
                .get(this.authService.organization?.id)
                .then((contracts: StripeContract[]) => {
                    this.contracts = contracts?.filter(contract => contract.status === StripeContractStatus.Activated);
                    this.isVoluntaryContractActivated = this.contracts?.some(
                        contract => contract.type === Enums.OrganizationContract.VOLUNTARY
                    );
                    return resolve();
                })
                .catch(error => {
                    this.$log.error("Error while fetching contracts", error);
                    return reject(error);
                });
        });
    }

    public getMonextContracts(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isClassicSale()) {
                return resolve();
            }

            return this.contractService
                .getMonext(this.authService.organization?.id)
                .then((contracts: MonextContract[]) => {
                    this.monextContracts = contracts?.filter(contract => contract.status === StripeContractStatus.Activated);
                    return resolve();
                })
                .catch(error => {
                    this.$log.error("Error while fetching monext contracts", error);
                    return reject(error);
                });
        });
    }

    public save(field) {
        this.isSaving = true;

        return this.saleService
            .update(this.id, this.setFieldDataOnSave(field))
            .then(async res => {
                this.$log.info(`Sale updated with success: ${field}`, res);
                this.resource = this.resourceFormatters(res);
                this.originalResource = angular.copy(this.resource);

                await this.getSaleTranslationStatus();
                this.$rootScope.$emit(Events.SaleUpdated);
            })
            .catch(error => {
                this.$log.error("Error while updating the sale", error);
                return this.$q.reject(error);
            })
            .finally(() => (this.isSaving = false));
    }

    public savetranslation(field) {
        this.isSaving = true;

        return this.saleService
            .translate(this.id, this.setFieldDataOnSave(field))
            .then(async res => {
                this.$log.info(`Sale translation updated with success: ${field}`, res);

                await this.getSaleTranslationStatus();
                this.$rootScope.$emit(Events.SaleUpdated);
            })
            .catch(error => {
                this.$log.error("Error while updating the translation", error);
                return this.$q.reject(error);
            })
            .finally(() => (this.isSaving = false));
    }

    public saveAddressFields(fields) {
        const mandatoryFields = [
            "address.formatted_address",
            "address.street",
            "address.postcode",
            "address.route",
            "address.street_number",
            "address.city",
            "address.location"
        ];

        const data = pick(this.originalResource, mandatoryFields);
        merge(data, pick(this.resource, fields));

        return this.saleService
            .update(this.id, data)
            .then(res => {
                this.$log.info("Sale address fields updated with success", res, fields);
                this.resource = this.resourceFormatters(res);
                this.originalResource = angular.copy(this.resource);
                this.$rootScope.$emit(Events.SaleUpdated);
            })
            .catch(error => {
                this.$log.error("Error while updating address fields", error, fields);
                return this.$q.reject(error);
            });
    }

    public publishSale() {
        return this.saleService.publish(this.id)
            .then((res: SalePublishRes) => {
                this.$log.info(`Sale ${this.id} published with success`, res);
                return this.fetchAll();
            })
            .catch(error => {
                this.$log.error(`Error while publishing the sale ${ this.id }`, error);
                return this.$q.reject(error);
            });
    }

    public setMediaResults(file) {
        const content = {
            type: "sale_results",
            meta: {
                name: file.name
            }
        };

        return this.uploadService.uploadFile(file, content)
            .then(media => {
                 return this.saleService.setMediaResults(this.id, media.id);
            })
            .then(mediaResult => {
                this.$log.info(`Sale ${this.id} results updated with success:`, mediaResult);
                return this.saleService.publish(this.id);
            })
            .then(publishedSale => {
                this.resource.sale_results = publishedSale.sale_results;
                this.resource.last_updated = publishedSale.last_updated;
            })
            .catch(error => {
                this.$log.error("Error while uploading a file (could be sale.setMediasResults or sale.publish)", error);
                return this.$q.reject(error);
            });
    }

    public deleteMediaResults() {
        return this.saleService
            .deleteMediaResults(this.id)
            .then(() => {
                return this.saleService.publish(this.id);
            })
            .then(publishedSale => {
                this.resource.sale_results = publishedSale.sale_results;
                this.resource.last_updated = publishedSale.last_updated;
            });
    }

    public setMediaCatalog(file) {
        const content = {
            type: "sale_catalog",
            meta: {
                name: file.name
            }
        };

        return this.uploadService.uploadFile(file, content)
            .then(media => {
                return this.saleService.setMediaCatalog(this.id, media.id);
            })
            .then(mediaCatalog => {
                this.$log.info(`Sale ${this.id} catalog updated with success:`, mediaCatalog);
                this.resource.sale_catalog = mediaCatalog;
                this.$rootScope.$emit(Events.SaleRelatedObjectUpdated);
            })
            .catch(error => {
                this.$log.error("Error while uploading a file (could be sale.setMediaCatalog)", error);
                return this.$q.reject(error);
            });
    }

    public deleteMediaCatalog() {
        return this.saleService.deleteMediaCatalog(this.id).then(() => {
            this.resource.sale_catalog = null;
            this.$rootScope.$emit(Events.SaleRelatedObjectUpdated);
        });
    }

    public updateSaleConditions(saleConditionsId) {
        const originalSaleConditionsId = get(this.originalResource, "sale_conditions.id");
        return this.saleService
            .updateSaleConditions(this.id, originalSaleConditionsId, saleConditionsId)
            .then(saleConditions => {
                this.$log.info(`Sale ${this.id} sale's conditions updated with success:`, saleConditions);
                this.resource.sale_conditions = pick(saleConditions, ["id", "url"]);
                this.originalResource.sale_conditions = angular.copy(this.resource.sale_conditions);
                this.$rootScope.$emit(Events.SaleRelatedObjectUpdated);
            });
    }

    public saveContactField(field) {
        const mandatoryFields = [
            "contact.name",
            "contact.type",
            "contact.contacts"
        ];

        const data = pick(this.originalResource, mandatoryFields);
        merge(data, pick(this.resource, field));

        return this.saleService
            .update(this.id, data)
            .then(res => {
                this.$log.info("Sale specific contact field, updated with success", res, field);
                this.resource = this.resourceFormatters(res);
                this.originalResource = angular.copy(this.resource);
                this.$rootScope.$emit(Events.SaleUpdated);
            })
            .catch(error => {
                this.$log.error("Error while updating field on specific contact", error, field);
                return this.$q.reject(error);
            });
    }

    public create(object) {
        return this.saleService.create(object);
    }

    public createItem(itemPost) {
        return this.saleService.createItem(this.id, itemPost);
    }

    public async insertItem(item) {
        this.isSaving = true;
        this.items.unshift(item);
        await this.getSaleTranslationStatus();
        this.$rootScope.$broadcast(Events.ItemUpdated, item);
        this.$rootScope.$broadcast(Events.PostMessageToV2);
        this.isSaving = false;
    }

    public reset() {
        const promises = [];

        this.items.forEach(item => {
            if (item.status !== Enums.ItemStatus.Confirmed) {
                promises.push(this.itemService.reset(item.id));
            }
        });

        if (this.resource.status !== Enums.SaleStatus.Published) {
            promises.push(this.saleService.reset(this.id));
        }

        return this.$q.all(promises)
            .then(() => {
                this.$log.info(`Sale ${ this.id } reset with success`);
                return this.fetchAll();
            })
            .catch(error => {
                this.$log.error(`Error while resetting the sale ${ this.id }`, error);
                return this.$q.reject(error);
            });
    }

    public standardSave(field) {
        if (
            this.resource?.isPast === false &&
            !angular.equals(pick(this.resource, field), pick(this.originalResource, field))
        ) {
            return this.save(field).catch(error => this.$q.reject(error));
        } else {
            return this.$q.resolve();
        }
    }

    public translationSave(field) {
        if (this.resource?.isPast === false) {
            return this.savetranslation(field).catch(error => this.$q.reject(error));
        } else {
            return this.$q.resolve();
        }
    }

    public isCommissionRateFilled() {
        return this.saleService.isCommissionRateFilled(this.resource);
    }

    public isShippingFilled(): boolean {
        return this.saleService.isShippingFilled(this.resource);
    }

    protected checkIfEveryMandatoryFieldsAreFilled() {
        this.setItemsErrors();
        this.setItemsPremiumErrors();
        const checkItems = this.checkItemsMandatoryFields();

        this.isEveryMandatoryFieldsFilled =
            this.saleService.checkIfEveryMandatoryFieldsAreFilled(this.resource) &&
            this.isLiveMandatoryFieldsFilled() &&
            checkItems &&
            !this.itemsPublishSummary.errors.length &&
            !this.itemsPublishSummary.premium_errors.length;
    }

    protected isOperatorMissingField(): boolean {
        return this.isLiveSale() &&
            Boolean(!this.liveSale?.operator_id && !this.liveSale?.is_external_operator_requested);
    }

    protected isContractMissing(): boolean {
        if (new Date(this.resource.datetime) < new Date(this.stripeEnd)) {
            const stripeVoluntaryContract = this.contracts?.find((c) => c.provider === "stripe" && c.status === "activated");
            return this.isLiveSale() && Boolean(stripeVoluntaryContract && !this.resource.contract_id);
        } else {
            return this.isLiveSale() && Boolean(this.monextContracts?.length && !this.resource.contract_id);
        }
    }

    protected isLiveMandatoryFieldsFilled(): boolean {
        return !this.isOperatorMissingField() && !this.isContractMissing();
    }

    protected isClassicSale() {
        return !(this.isLiveSale() || this.isOnlineSale());
    }

    protected isLiveSale() {
        return [this.resource?.sites?.auction?.type, this.resource?.sites?.interencheres?.type].includes(
            Enums.AuctionType.Live
        );
    }

    protected isOnlineSale() {
        return [this.resource?.sites?.auction?.type, this.resource?.sites?.interencheres?.type].includes(
            Enums.AuctionType.Online
        );
    }

    /**
     * Build the sale's resource for our view
     * @param sale {object} The object that contain sale's informations
     *
     * @returns {object} Returns formatted sale for our view
     */
    protected resourceFormatters(sale) {
        sale.isPast = false;

        if (sale.datetime && !moment(sale.datetime).isSameOrAfter(moment(), "day")) {
            sale.isPast = true;
        }

        if (new Date(sale.start_at) < new Date()) {
            sale.startedToday = true;
        } else {
            sale.startedToday = false;
        }

        sale.field = this.formatCategoryField(sale.category_id);
        sale.address = this.formatAddress(sale.address);
        sale.exhibitions = sale.exhibitions ? sale.exhibitions : [];

        return sale;
    }

    protected hasNonPublishedItems() {
        return this.items.some(item => {
            return (item.status !== "confirmed");
        });
    }

    protected checkIsPublishable() {
        this.isPublishable = this.resource?.status !== Enums.SaleStatus.Published || this.hasNonPublishedItems();
    }

    protected getItemsPublishCount(): number {
        return this.items?.filter((i: thor.ItemGet) => !i.states.hidden && !i.states.suppressed).length ?? 0;
    }

    protected formatAddress(address) {
        let formattedAddress = null;

        if (address) {
            formattedAddress = address;
            if (address.secret) {
                formattedAddress.secret = new Date(address.secret);
            }
        }

        return formattedAddress;
    }

    protected formatCategoryField(categoryId) {
        let field = null;

        if (categoryId) {
            field = this.categoryService.getCategoryField(categoryId);
        }

        return field;
    }

    protected setFieldDataOnSave(field: string | string[]) {
        let data = pick(this.resource, field);

        // handle timezone dependency on start_at|datetime reset
        const dateFields = ["start_at", "datetime"];
        if (
            (
                typeof field === "string" && dateFields.includes(field) ||
                Array.isArray(field) && dateFields.some(v => field.includes(v))
            ) && (
                data.start_at === null ||
                data.datetime === null
            )
        ) {
            data = { ...data, ...pick(this.resource, "timezone") };
        }

        if (field === "conditions") {
            delete data.conditions.payment_translations;
            delete data.conditions.sales_translations;
        }

        if (field === "exhibitions" && (this.resource.source_lang !== this.selectedTargetLanguage)) {
            data = {
                "exhibitions": data.exhibitions.map(item => ({
                    "description_translations": Object.fromEntries(
                        Object.entries(item.description_translations)
                        .filter(([key]) => key === LocaleCode.fr_FR || key === LocaleCode.en_US)
                    )
                })).filter(item => Object.keys(item.description_translations).length > 0) as any
            };
        }

        return data;
    }

    protected setItemsErrors() {
        const items = this.items.filter(item => !item.states?.suppressed);
        const itemsOrderNumberUniq = uniqBy(items, i => this.utils.getOrderNumberNormalized(i));
        const diff = difference(items, itemsOrderNumberUniq).filter(i => i.meta?.order_number?.primary);

        this.itemsPublishSummary.errors = diff.map(item => {
            const orderNbr = this.utils.getOrderNumberNormalized(item);
            return {
                orderNbr,
                item,
                message: this.$t("un_lot_a_deja_ce_numero_d_ordre", [orderNbr])
            };
        });
    }

    protected setItemsPremiumErrors() {
        const items = this.items.filter(item => !item.states?.suppressed);

        this.itemsPublishSummary.premium_errors = items.reduce((acc, item) => {
            if (
                item.asked_for_premium &&
                item.premium_status !== PremiumStatus.Confirmed &&
                (!this.categoryService.checkPremiumCriteria(item) ||
                    (this.categoryService.isCategoryDeliverable(item) && isSaleWithoutShipping(this.resource)))
            ) {
                const concernedItem = { orderNbr: this.utils.getOrderNumberNormalized(item), item, message: [] };

                if (!this.categoryService.isCategoryPremium(item)) {
                    concernedItem.message.push(this.$t("refusal_reasons.category"));
                }
                if (item.description?.length < PREMIUM_DESCRIPTION_MIN_LENGTH) {
                    concernedItem.message.push(this.$t("refusal_reasons.description"));
                }
                if (!item.media_count) {
                    concernedItem.message.push(this.$t("refusal_reasons.media"));
                }
                if (isSaleWithoutShipping(this.resource)) {
                    concernedItem.message.push(this.$t("refusal_reasons.shipping"));
                }
                acc.push(concernedItem);
            }
            return acc;
        }, []);
    }

    protected async setSubscriptionsCount() {
        if (this.isClassicSale()) {
            return;
        }
        this.subscriptionsCount = (
            await this.gatewayApi.countSubscriptions(
                this.id,
                this.isOnlineSale() ? Enums.AuctionType.Online : Enums.AuctionType.Live
            )
        )?.count;
    }

    protected postMessageToV2() {
        const iframe = document.querySelector(".iframe-container iframe") as HTMLIFrameElement;
        iframe?.contentWindow.postMessage(
            { saleId: parseInt(this.id as unknown as string, 10) },
            window.location.origin
        );
    }

    private checkItemsMandatoryFields() {
        let foundInvalidItem: boolean = false;
        this.items.forEach(item => {
            item.valid = !!(get(item, "states.suppressed") ||
                item.description &&
                item.description.length >= this.itemDescriptionMinLength);
            foundInvalidItem = foundInvalidItem || !item.valid;
        });
        return !foundInvalidItem;
    }
}
